import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContext';

/**
 * Custom hook to fetch comments for a specific day
 * @param {number} dayId - The ID of the day to fetch comments for
 * @param {object} options - Additional options for the query
 */
export const useComments = (dayId, options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useUser();
  
  return useQuery({
    queryKey: ['comments', dayId],
    queryFn: () => menuService.getComments(dayId),
    enabled: !!dayId, // Only run the query if dayId is provided
    // Customize refetch behavior for comments
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus
    // Handle error display
    onError: (error) => {
      console.error(`Error fetching comments for day ${dayId}:`, error);
    },
    // Allow custom onSuccess in options to run first before default processing
    onSuccess: (data) => {
      console.log(`Comments fetched for day ${dayId}`);
      
      // If we have no actual data, don't try to update
      if (!data || !Array.isArray(data) || !user) return;
      
      // Also update the 'has_unread_comments' flag in any menu cache that contains this day
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'menu',
        // Don't refetch, just invalidate
        refetchType: 'none'
      });
    },
    ...options
  });
};

/**
 * Custom hook to mark all comments for a day as read
 * @param {object} options - Additional options for the mutation
 */
export const useMarkCommentsAsRead = (options = {}) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation(); // Get language context
  
  return useMutation({
    mutationFn: ({ dayId, menuId }) => menuService.markCommentsAsRead(dayId), // Accept menuId
    
    onSuccess: (data, { dayId, menuId }) => {
      console.log(`Comments for day ${dayId} marked as read on server`);
      const language = i18n.language || 'en';
      const menuQueryKey = ['menu', menuId, language];

      // Optimistically update the specific menu cache
      const currentMenuData = queryClient.getQueryData(menuQueryKey);
      if (currentMenuData && currentMenuData.days) {
        console.log(`[OptimisticUpdate] Updating has_unread_comments for day ${dayId} in menu ${menuId}`);
        const updatedDays = currentMenuData.days.map(day => {
          if (day.id === dayId) {
            return { ...day, has_unread_comments: false };
          }
          return day;
        });
        queryClient.setQueryData(menuQueryKey, { ...currentMenuData, days: updatedDays });
      } else {
        console.log(`[OptimisticUpdate] Menu cache not found or invalid for key:`, menuQueryKey);
      }
      
      // Restore invalidation of the comments query itself.
      queryClient.invalidateQueries(['comments', dayId]);
      
      // Invalidate menu queries to ensure consistency and update indicators everywhere
      // This serves as a fallback and ensures other related data is updated
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'menu',
      });
      
      if (options.onSuccess) {
        options.onSuccess(data, { dayId, menuId });
      }
    },
    
    onError: (error, { dayId, menuId }) => {
      console.error(`Error marking comments as read for day ${dayId}:`, error);
      
      // Restore refetch comments on error.
      queryClient.invalidateQueries(['comments', dayId]);
      
      if (options.onError) {
        options.onError(error, { dayId, menuId });
      }
    }
  });
};

/**
 * Custom hook to add a comment to a specific day
 * @param {object} options - Additional options for the mutation
 */
export const useAddComment = (options = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useUser();
  
  return useMutation({
    mutationFn: ({ dayId, text }) => menuService.addComment(dayId, text),
    
    // Implement optimistic updates
    onMutate: async ({ dayId, text }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries(['comments', dayId]);
      
      // Snapshot the current value
      const previousComments = queryClient.getQueryData(['comments', dayId]);
      
      // Create an optimistic comment
      const optimisticComment = {
        id: `temp-${Date.now()}`, // Temporary ID
        text,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_unread: false, // Not unread for the author
        user_id: user?.id,
        author_name: user?.name || user?.username || t('menu.currentUser', 'You'),
        // Add any other required fields
        _isOptimistic: true // Flag to identify optimistic entries
      };
      
      // Optimistically update the cache
      if (previousComments) {
        queryClient.setQueryData(['comments', dayId], [
          optimisticComment, // Add to the beginning (newest first)
          ...previousComments
        ]);
      } else {
        queryClient.setQueryData(['comments', dayId], [optimisticComment]);
      }
      
      // Also optimistically update comment counts in menu queries
      // This is a simplified approach - a more complete one would update specific menu items
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'menu',
        // Don't refetch, just invalidate
        refetchType: 'none'
      });
      
      return { previousComments };
    },
    
    // When the mutation succeeds, invalidate the comments query for this day
    onSuccess: (data, variables, context) => {
      const { dayId } = variables;
      console.log(`Comment added successfully for day ${dayId}:`, data);
      
      // Invalidate comments for this day to get the real data from server
      queryClient.invalidateQueries(['comments', dayId]);
      
      // We also need to invalidate any menu queries that contain this day
      // to update the unread comments indicator
      queryClient.invalidateQueries({
        predicate: (query) => {
          // Invalidate any menu query
          return query.queryKey[0] === 'menu';
        }
      });
      
      // Call user-provided onSuccess callback if it exists
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    
    // If the mutation errors, roll back to the previous value
    onError: (error, variables, context) => {
      console.error(`Error adding comment for day ${variables.dayId}:`, error);
      
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', variables.dayId], context.previousComments);
      }
      
      // Call user-provided onError callback if it exists
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    
    ...options
  });
};

/**
 * Custom hook to edit an existing comment
 * @param {object} options - Additional options for the mutation
 */
export const useEditComment = (options = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: ({ dayId, commentId, text }) => menuService.editComment(dayId, commentId, text),
    
    onMutate: async ({ dayId, commentId, text }) => {
      // Cancel any outgoing refetches for this comment
      await queryClient.cancelQueries(['comments', dayId]);
      
      // Snapshot the previous value
      const previousComments = queryClient.getQueryData(['comments', dayId]);
      
      // Optimistically update the cache with the new comment text
      if (previousComments) {
        queryClient.setQueryData(['comments', dayId], 
          previousComments.map(comment => 
            comment.id === commentId 
              ? { 
                  ...comment, 
                  text, 
                  updated_at: new Date().toISOString() 
                } 
              : comment
          )
        );
      }
      
      return { previousComments };
    },
    
    // If the mutation fails, roll back the optimistic update
    onError: (error, variables, context) => {
      console.error(`Error editing comment for day ${variables.dayId}, comment ${variables.commentId}:`, error);
      
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', variables.dayId], context.previousComments);
      }
      
      // Call user-provided onError callback if it exists
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    
    // When the mutation succeeds, invalidate the comments query for this day
    onSuccess: (data, variables) => {
      const { dayId } = variables;
      console.log(`Comment edited successfully for day ${dayId}:`, data);
      
      // Invalidate comments for this day to trigger a refetch
      queryClient.invalidateQueries(['comments', dayId]);
      
      // Call user-provided onSuccess callback if it exists
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    
    ...options
  });
}; 